import React from "react";
import { Role } from "../../../../services/roles.service";

interface Props {
  roles:          Role[];
  selectedId:     string | null;
  onSelect:       (roleId: string) => void;
  onCreateNew:    () => void;
  onClone:        (role: Role) => void;
  /**
   * Whether the user holds roles.manage. Creating and cloning both POST to
   * /clinic/rbac/roles, which requires it — so without the grant these controls
   * can only ever 403, and showing them invites the user to fill in a modal that
   * cannot save. Selecting a role to READ stays available (that is
   * settings_access.view).
   */
  canManage:      boolean;
}

const RoleRow: React.FC<{
  role: Role; selected: boolean; onSelect: () => void; onClone: () => void; canClone: boolean;
}> = ({ role, selected, onSelect, onClone, canClone }) => (
  <div
    onClick={onSelect}
    className={`group flex items-center justify-between gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
      selected ? "bg-blue-50 border border-blue-200" : "hover:bg-gray-50 border border-transparent"
    }`}
  >
    <div className="min-w-0">
      <p className="text-sm font-medium text-gray-900 truncate">{role.name}</p>
      {role.description && <p className="text-xs text-gray-400 truncate">{role.description}</p>}
    </div>
    {canClone && (
      <button
        type="button"
        onClick={e => { e.stopPropagation(); onClone(); }}
        title="Clone this role"
        className="opacity-0 group-hover:opacity-100 text-xs font-medium text-blue-600 hover:text-blue-700 flex-shrink-0 transition-opacity"
      >
        Clone
      </button>
    )}
  </div>
);

export const RoleList: React.FC<Props> = ({ roles, selectedId, onSelect, onCreateNew, onClone, canManage }) => {
  // Same role name appears twice by design once a clinic clones a template
  // (its own editable copy + the read-only original) - group them under
  // separate headings instead of interleaving by name, or "Doctor" x2 with
  // no visual distinction reads as a bug.
  const clinicRoles = roles.filter(r => !r.is_system);
  const systemTemplates = roles.filter(r => r.is_system);

  return (
    <div className="space-y-3">
      <div>
        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide px-3 mb-1">Your Roles</p>
        <div className="space-y-1.5">
          {clinicRoles.map(role => (
            <RoleRow key={role.role_id} role={role} selected={selectedId === role.role_id}
              onSelect={() => onSelect(role.role_id)} onClone={() => onClone(role)}
              canClone={canManage} />
          ))}
        </div>
      </div>
      {canManage && (
        <button
          type="button"
          onClick={onCreateNew}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold text-blue-600 border border-dashed border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          New Role
        </button>
      )}
      {systemTemplates.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide px-3 mb-1">System Templates (read-only)</p>
          <div className="space-y-1.5">
            {systemTemplates.map(role => (
              <RoleRow key={role.role_id} role={role} selected={selectedId === role.role_id}
                onSelect={() => onSelect(role.role_id)} onClone={() => onClone(role)}
              canClone={canManage} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
